import { useState, type FC } from 'react';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Button, Link, Space, Text } from '@g2u/ui-kit';
import { type TableProps } from 'antd';

import { CustomerSearchModal, Section, CustomerModalTitle } from 'components';
import { Condition, type Customer, type WithOptionalDataTest } from 'types';
import { BookingStore, RootStore } from 'store';
import { getCustomerTag } from 'helpers';
import { useText } from 'helpers/hooks/useText';

type Props = TableProps<any> & WithOptionalDataTest;

export const BookingFlowSelection: FC<Props> = observer(() => {
  const navigate = useNavigate();
  const { customer, setCustomer, setIsNewCustomer } = BookingStore;
  const { user } = RootStore;
  const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);
  const { getText } = useText();

  return (
    <Section title="Confirm If New / Repeat Customer">
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} fullWidth>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} className="script-box">
          <Text.$ size={Text.Size.SM} mark>
            {getText('booking.start.accountTypeConfirmation')}
          </Text.$>
        </Space.$>
        <Space.$ direction={Space.Direction.Horizontal}>
          <Button.$
            type={Button.Type.Primary}
            onClick={() => {
              setIsNewCustomer(true);
              setCustomer(null);
              navigate('/booking/location');
            }}
            dataTestId="start-new-customer-flow"
          >
            New Customer Booking
          </Button.$>
          <Button.$
            type={Button.Type.Primary}
            onClick={() => {
              setIsNewCustomer(false);
              setShowCustomerSearchModal(true);
            }}
            dataTestId="start-repeat-customer-flow"
          >
            Repeat Customer Booking
          </Button.$>
        </Space.$>
      </Space.$>

      <CustomerSearchModal
        hasDiscount={true}
        customer={customer as Customer}
        modalTitle={
          customer ? (
            <CustomerModalTitle customerId={customer.ID} notes={customer.special_notes} />
          ) : (
            'Select Customer File'
          )
        }
        hasScript
        isOpen={showCustomerSearchModal}
        onOk={() => {
          setShowCustomerSearchModal(false);
          navigate('/booking/location');
        }}
        onCancel={() => {
          setShowCustomerSearchModal(false);
        }}
        actions={[
          (customer) => getCustomerTag(customer),
          (customer) => (
            <Link.$
              key={`${customer.ID}-select`}
              onClick={(e) => {
                e.preventDefault();
                setCustomer(customer);
                setIsNewCustomer(false);
              }}
              disabled={
                ([customer?.overdue, customer?.bad_debt].includes(Condition.Yes) && !user?.overdue_override) ||
                customer?.blacklist === Condition.Yes
              }
            >
              Select
            </Link.$>
          ),
        ]}
        emptyContent={() => (
          <Button.$
            type={Button.Type.Primary}
            onClick={() => {
              setIsNewCustomer(true);
              setCustomer(null);
              setShowCustomerSearchModal(false);
              navigate('/booking/location');
            }}
            dataTestId="new-customer-booking-btn"
          >
            Book a job for a new customer
          </Button.$>
        )}
      />
    </Section>
  );
});
